import { TDocumentDefinitions } from 'pdfmake/interfaces';

import envs from '@/infrastructure/config/envs';

import { showDayMonthYearAsText } from './format-date';
export interface Certificate {
  guid?: string;
  topicName?: string;
  studentName?: string;
  workload?: number;
  completeIn?: Date;
  expirationIn?: Date;
  certificateSettings?: {
    id?: string;
    logoImageUrl?: string;
    logoPartnerImageUrl?: string;
    assignImageUrl?: string;
    assignPartnerImageUrl?: string;
    backgroundImageUrl: string;
    minimumProgressPercentage?: string;
    courseApprovalPercentage?: string;
  };
}

const cloudfrontUrl = envs.cloudfrontUrl;

export const pdfFonts = {
  VanguardCF: {
    normal: `${cloudfrontUrl}/fonts/vanguard-cf/vanguard-cf-regular.ttf`,
    semibold: `${cloudfrontUrl}/fonts/vanguard-cf/vanguard-cf-semibold.ttf`,
    bold: `${cloudfrontUrl}/fonts/vanguard-cf/vanguard-cf-bold.ttf`,
    medium: `${cloudfrontUrl}/fonts/vanguard-cf/vanguard-cf-regular.ttf`,
  },

  WorkSans: {
    normal: `${cloudfrontUrl}/fonts/WorkSans/WorkSans-Regular.ttf`,
    semibold: `${cloudfrontUrl}/fonts/WorkSans/WorkSans-SemiBold.ttf`,
    bold: `${cloudfrontUrl}/fonts/WorkSans/WorkSans-Bold.ttf`,
    medium: `${cloudfrontUrl}/fonts/WorkSans/WorkSans-Regular.ttf`,
  },

  AprovaSans: {
    normal: `${cloudfrontUrl}/fonts/aprova-sans/aprova-sans-regular.ttf`,
    semibold: `${cloudfrontUrl}/fonts/aprova-sans/aprova-sans-bold.ttf`,
    bold: `${cloudfrontUrl}/fonts/aprova-sans/aprova-sans-bold.ttf`,
    medium: `${cloudfrontUrl}/fonts/aprova-sans/aprova-sans-regular.ttf`,
  },
};

const getMicroCertStyles = {
  documentName: {
    fontSize: 60,
    alignment: 'center',
    bold: true,
    color: '#111111',
    font: 'VanguardCF',
  },
  topicName: {
    font: 'AprovaSans',
    fontSize: 30,
    bold: true,
  },
  workload: {
    font: 'AprovaSans',
    fontSize: 25,
    color: '#999999',
  },
  studentName: {
    font: 'AprovaSans',
    fontSize: 20,
    color: '#111111',
    bold: true,
  },
  completeIn: {
    font: 'AprovaSans',
    fontSize: 16,
    color: '#111111',
    bold: true,
  },
  validationLink: {
    fontSize: 10,
    font: 'AprovaSans',
    color: '#111111',
  },
  certificateValidDate: {
    fontSize: 10,
    font: 'AprovaSans',
    bold: true,
    color: '#111111',
  },
};

export const getBase64FromUrl = async (url: string): Promise<any> => {
  const data = await fetch(url);
  const blob = await data.blob();
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
  });
};

const certificateText = {
  certificate: 'certificado',
  workloadInHours: 'horas de carga horária',
  workloadInHour: 'hora de carga horária',
  partnerText: 'Em parceria com:',
  courseCompletionDate: 'completou o curso em',
  linkDigitalValidationText: 'Link único de validação do certificado',
  dateDigitalValidationText: 'Certificado válido até {{expirationIn}}',
};

export const getMicroCertificateDocumentDefinition = async (
  data: Certificate,
  certificateURL: string,
): Promise<any> => {
  const {
    topicName,
    studentName,
    workload,
    completeIn,
    expirationIn,
    certificateSettings: { backgroundImageUrl },
  } = data;
  const [backgroundDocumentImage] = await Promise.all([getBase64FromUrl(backgroundImageUrl)]);

  const WorkloadText =
    workload === 1
      ? workload + ' ' + certificateText.workloadInHour
      : workload + ' ' + certificateText.workloadInHours;

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    background: (_currentPage: any, pageSize: any) => ({
      image: backgroundDocumentImage,
      width: pageSize.width,
      height: pageSize.height,
    }),
    pageMargins: [25, 25, 25, 25],
    styles: getMicroCertStyles,
    content: [
      {
        columns: [
          {
            width: '70%',
            stack: [
              {
                absolutePosition: { x: 30, y: 180 },
                text: WorkloadText,
                style: ['workload'],
              },
              {
                margin: [5, 190, 0, 0],
                text: `${topicName}`,
                style: ['topicName'],
              },
              {
                text: `${studentName},`,
                absolutePosition: { x: 30, y: 380 },
                style: ['studentName'],
              },
              {
                absolutePosition: { x: 30, y: 410 },
                text: `completou o curso em ${showDayMonthYearAsText(new Date(completeIn))}`,
                style: ['completeIn'],
              },
              {
                qr: `${certificateURL}`,
                absolutePosition: { x: 695, y: 430 },
                background: '#fff',
                fit: 120, // this is the size of the QR Code - set exactly to fit in the position
                eccLevel: 'Q',
              },
              {
                absolutePosition: { x: 577, y: 550 },
                text: `Certificado válido até ${showDayMonthYearAsText(new Date(expirationIn))}`,
                style: ['certificateValidDate'],
              },
            ],
          },
        ],
      },
    ],
    defaultStyle: {
      font: 'AprovaSans',
    },
  } as unknown as TDocumentDefinitions;
};

export const printPdf = (dataUrl: string): void => {
  const targetElement = document.querySelector('#print');
  const iframe = document.createElement('iframe');
  iframe.src = dataUrl;
  iframe.width = '100%';
  iframe.height = '100%';

  targetElement?.appendChild(iframe);
};
